import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

/**
 * Toast notifications globales. Un seul élément dans le DOM, exposé via
 * l'API `showToast(message, options?)`.
 */

interface ToastState {
  id: number;
  message: string;
  level: 'info' | 'error' | 'success';
}

let toastInstance: Cof2Toast | null = null;
let nextId = 1;

export function showToast(
  message: string,
  options: { level?: 'info' | 'error' | 'success'; durationMs?: number } = {},
): void {
  toastInstance?.push(
    { id: nextId++, message, level: options.level ?? 'info' },
    options.durationMs ?? 3500,
  );
}

@customElement('cof2-toast')
export class Cof2Toast extends LitElement {
  @state() private toasts: ToastState[] = [];

  static styles = css`
    :host {
      position: fixed;
      bottom: var(--space-4);
      right: var(--space-4);
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
      z-index: 200;
      pointer-events: none;
    }
    .toast {
      pointer-events: auto;
      background: var(--color-bg-elev-2);
      color: var(--color-fg);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      padding: var(--space-2) var(--space-3);
      min-width: 200px;
      max-width: 360px;
      box-shadow: 0 8px 24px rgb(0 0 0 / 0.3);
    }
    .toast.error { border-color: var(--color-danger); }
    .toast.success { border-color: var(--color-success); }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    toastInstance = this;
    this.setAttribute('role', 'status');
    this.setAttribute('aria-live', 'polite');
    this.setAttribute('aria-atomic', 'true');
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (toastInstance === this) toastInstance = null;
  }

  push(t: ToastState, durationMs: number): void {
    this.toasts = [...this.toasts, t];
    setTimeout(() => {
      this.toasts = this.toasts.filter((x) => x.id !== t.id);
    }, durationMs);
  }

  render() {
    return html`
      ${this.toasts.map((t) => html`<div class="toast ${t.level}">${t.message}</div>`)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cof2-toast': Cof2Toast;
  }
}
